import { useState } from 'react';
import { Button, TextInput } from '@c2fo/liquidity';
import { EyeOpenIcon, EyeCrossedIcon } from '@c2fo/liquidity/icons';
import { Modal } from '../Modal';
import { FormField } from '../FormField';

interface ChangePasswordModalProps {
  isOpen: boolean;
  accountName: string;
  accountDisplayName: string;
  onClose: () => void;
  onSave: (accountName: string, newPassword: string) => Promise<boolean>;
  onError: (message: string) => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  accountName,
  accountDisplayName,
  onClose,
  onSave,
  onError,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isFormValid = () => {
    return newPassword.length >= 8 && newPassword === confirmPassword;
  };

  const handleSave = async () => {
    if (!newPassword) {
      onError('Password is required');
      handleClose();
      return;
    }

    if (newPassword.length < 8) {
      onError('Password must be at least 8 characters');
      handleClose();
      return;
    }

    if (newPassword !== confirmPassword) {
      onError('Passwords do not match');
      handleClose();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(accountName, newPassword);
      handleClose();
    } catch {
      onError('Failed to update password');
      handleClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Change Password"
      maxWidth="md"
      footer={
        <>
          <Button variant="tertiary" className="rounded-md" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="rounded-md text-white"
            onClick={handleSave}
            disabled={isSaving || !isFormValid()}
          >
            {isSaving ? 'Saving...' : 'Update Password'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Change password for <span className="font-medium">{accountDisplayName}</span>
        </p>

        <FormField label="New Password" required htmlFor="newPassword">
          <div className="relative">
            <TextInput
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
              className="w-full pr-10"
              size="sm"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showNewPassword ? (
                <EyeOpenIcon className="h-5 w-5 stroke-2" />
              ) : (
                <EyeCrossedIcon className="h-5 w-5 stroke-2" />
              )}
            </button>
          </div>
        </FormField>

        <FormField
          label="Confirm Password"
          required
          htmlFor="confirmPassword"
          error={
            newPassword && confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : undefined
          }
        >
          <div className="relative">
            <TextInput
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full pr-10"
              size="sm"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? (
                <EyeOpenIcon className="h-5 w-5 stroke-2" />
              ) : (
                <EyeCrossedIcon className="h-5 w-5 stroke-2" />
              )}
            </button>
          </div>
        </FormField>
      </div>
    </Modal>
  );
};
